import { shallowRef } from 'vue';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { mapStyle } from '../utils/mapStyle';
import { buildPinIcon, buildClusterRenderer } from '../utils/markerIcons';
import { isMapsDisabled, registerDebugHelper } from '../utils/debugMode';

let optionsInitialized = false;
let librariesPromise = null;

// lazy load
function loadGoogleMapsLibraries(apiKey) {
	if (!optionsInitialized) {
		setOptions({ key: apiKey, v: 'weekly' });
		optionsInitialized = true;
	}

	if (!librariesPromise) {
		librariesPromise = Promise.all([
			importLibrary('maps'),
			importLibrary('places'),
			importLibrary('geocoding'),
		]).then(() => window.google);
	}

	return librariesPromise;
}

// debug without Google Maps API
function renderDisabledPlaceholder(container) {
	container.innerHTML = '';
	const el = document.createElement('div');
	el.className = 'dealer-locator__map-debug';
	el.textContent = 'Google Maps disabled (debug mode) — window.dealerLocatorDebug.enableMaps()';
	container.appendChild(el);
}

function boundsToPlain(bounds) {
	if (!bounds) return null;
	const ne = bounds.getNorthEast();
	const sw = bounds.getSouthWest();
	return { north: ne.lat(), east: ne.lng(), south: sw.lat(), west: sw.lng() };
}

export function useGoogleMaps(apiKey) {
	registerDebugHelper();

	const google = shallowRef(null);
	const map = shallowRef(null);
	const clusterer = shallowRef(null);
	let markers = [];

	// save map scale
	let savedView = null;

	// open dealer detail
	let dimOthers = false;

	let suppressIdleUntil = 0;
	function markProgrammaticMove() {
		suppressIdleUntil = Date.now() + 800;
	}

	// lazy map init
	async function initMap(container, { lat, lng, zoom }) {
		if (isMapsDisabled()) {
			renderDisabledPlaceholder(container);
			return { google: null, map: null };
		}

		const sdk = await loadGoogleMapsLibraries(apiKey);
		google.value = sdk;

		map.value = new sdk.maps.Map(container, {
			center: { lat, lng },
			zoom,
			styles: mapStyle,
			backgroundColor: '#1E1E1E',
			disableDefaultUI: true,
			zoomControl: true,
			gestureHandling: 'greedy',
			clickableIcons: false,
		});

		markProgrammaticMove();

		clusterer.value = new MarkerClusterer({
			map: map.value,
			renderer: buildClusterRenderer(sdk, { isDimmed: () => dimOthers }),
		});

		return { google: sdk, map: map.value };
	}

	function clearMarkers() {
		clusterer.value?.clearMarkers();
		markers.forEach((marker) => marker.setMap(null));
		markers = [];
		dimOthers = false;
	}

	// dealers marker
	function setDealerMarkers(dealers, { onSelect } = {}) {
		if (!google.value || !map.value) return;
		clearMarkers();

		markers = dealers.map((dealer) => {
			const marker = new google.value.maps.Marker({
				position: { lat: dealer.lat, lng: dealer.lng },
				icon: buildPinIcon(google.value),
				title: dealer.name,
				optimized: true,
			});

			marker.addListener('click', () => {
				onSelect?.(dealer);
			});

			marker.__dealerId = dealer.id;
			return marker;
		});

		clusterer.value?.addMarkers(markers);
	}

	// dealer details open
	function setActiveDealer(dealerId) {
		dimOthers = true;

		markers.forEach((marker) => {
			const isActive = marker.__dealerId === dealerId;
			marker.setIcon(buildPinIcon(google.value, { active: isActive, large: isActive }));
			marker.setOpacity(isActive ? 1 : 0.5);
			marker.setClickable(isActive);
			marker.setZIndex(isActive ? 9999 : 1);
		});

		clusterer.value?.render();
	}

	// back to full list
	function clearActiveDealer() {
		dimOthers = false;

		markers.forEach((marker) => {
			marker.setIcon(buildPinIcon(google.value));
			marker.setOpacity(1);
			marker.setClickable(true);
			marker.setZIndex(null);
		});

		clusterer.value?.render();
	}

	function panTo(lat, lng, zoom) {
		if (!map.value) return;
		markProgrammaticMove();
		map.value.panTo({ lat, lng });
		if (zoom) map.value.setZoom(zoom);
	}

	function saveMapView() {
		if (!map.value) return;
		const center = map.value.getCenter();
		if (!center) return;
		savedView = { center: { lat: center.lat(), lng: center.lng() }, zoom: map.value.getZoom() };
	}

	function restoreMapView() {
		if (!map.value || !savedView) return;
		markProgrammaticMove();
		map.value.panTo(savedView.center);
		map.value.setZoom(savedView.zoom);
		savedView = null;
	}

	// add all dealers markers in active zone
	function fitBounds(dealers) {
		if (!google.value || !map.value || !dealers.length) return;
		markProgrammaticMove();

		// not use fit bounds
		if (dealers.length === 1) {
			map.value.panTo({ lat: dealers[0].lat, lng: dealers[0].lng, });
			map.value.setZoom(8);
			return;
		}

		const bounds = new google.value.maps.LatLngBounds();

		dealers.forEach((dealer) => {
			bounds.extend({ lat: dealer.lat, lng: dealer.lng, });
		});

		map.value.fitBounds(bounds, 60);
	}

	function getBounds() {
		if (!map.value) return null;
		return boundsToPlain(map.value.getBounds());
	}

	function getCenter() {
		if (!map.value) return null;
		const center = map.value.getCenter();
		return center ? { lat: center.lat(), lng: center.lng() } : null;
	}

	function onUserBoundsChange(callback) {
		if (!map.value) return () => { };

		const listener = map.value.addListener('idle', () => {
			if (Date.now() < suppressIdleUntil) return;
			const bounds = getBounds();
			if (bounds) callback(bounds, getCenter());
		});

		return () => google.value.maps.event.removeListener(listener);
	}

	function waitForIdle() {
		return new Promise((resolve) => {
			if (!map.value || !google.value) {
				resolve();
				return;
			}
			google.value.maps.event.addListenerOnce(map.value, 'idle', resolve);
		});
	}

	// Google Places Autocomplete
	function attachAutocomplete(inputEl, onPlaceChanged) {
		if (!google.value) return () => { };

		const autocomplete = new google.value.maps.places.Autocomplete(inputEl, {
			fields: ['geometry', 'formatted_address', 'name', 'types'],
			types: ['geocode'],
			// componentRestrictions: { country: 'us' }, only USA
		});

		const listener = autocomplete.addListener('place_changed', () => {
			const place = autocomplete.getPlace();
			const location = place?.geometry?.location;
			if (!location) {
				onPlaceChanged(null);
				return;
			}
			onPlaceChanged({
				lat: location.lat(),
				lng: location.lng(),
				label: place.formatted_address || place.name || inputEl.value,
				types: place.types || [],
				viewport: boundsToPlain(place.geometry?.viewport),
			});
		});

		return () => google.value.maps.event.removeListener(listener);
	}

	async function geocodeQuery(query) {
		if (!google.value) return null;
		const geocoder = new google.value.maps.Geocoder();

		try {
			const { results } = await geocoder.geocode({ address: query });
			const first = results?.[0];
			if (!first) return null;

			return {
				lat: first.geometry.location.lat(),
				lng: first.geometry.location.lng(),
				label: first.formatted_address,
				types: first.types || [],
				viewport: boundsToPlain(first.geometry.viewport),
			};
		} catch {
			return null;
		}
	}

	return {
		google,
		map,
		initMap,
		setDealerMarkers,
		setActiveDealer,
		clearActiveDealer,
		clearMarkers,
		panTo,
		saveMapView,
		restoreMapView,
		fitBounds,
		getBounds,
		getCenter,
		onUserBoundsChange,
		waitForIdle,
		attachAutocomplete,
		geocodeQuery,
	};
}
